// ABOUTME: Compact clickable shipment element for the relation order summary sidepanel.
// ABOUTME: Shows the primary shipment; extra shipments peek out underneath in their shipper color.

import { Box, ButtonBase } from "@mui/material";
import { tokens } from "../../../../../theme";
import { getShipperLogo } from "../../../../../utils/logomapper";
import { general_states } from "../../../../../utils/staticEnums";
import { getShipperColorSettingsByType } from "../../../../Streams/StreamShipperSettings";
import Text from "../../../../theme/text/Text";

// Shipments that count for the stack: real outbound shipments only, so returns, preloaded
// labels and cancelled shipments are excluded (those remain visible in the shipments view).
// Sorted newest first; the first entry is the primary card.
export const stackableShipments = (shipments) =>
    [...(shipments ?? [])]
        .filter(s => !['return', 'preloaded'].includes(s.type) && s.state !== general_states.CANCELLED)
        .sort((a, b) => new Date(b.shipmentDate) - new Date(a.shipmentDate));

// Resolves the shipper to display for a shipment: forwarding shippers (cirro, sendcloud)
// hand the parcel to an external shipper, so that external shipper is shown instead.
// Mirrors the relation-environment resolution in the shipments view.
export const displayShipper = (shipment) => {
    let name = shipment?.shipper?.name;
    let type = shipment?.shipper?.type;

    const extNameLower = (shipment?.externalShipperName ?? "").toLowerCase();
    if (type === 'cirro') {
        type = extNameLower.includes("dhl")
            ? "dhl"
            : (extNameLower.includes("postnl") || extNameLower.includes("post-nl") || extNameLower.includes("post nl"))
            ? "postnl"
            : "default";

        name = extNameLower.includes("dhl")
            ? "DHL"
            : (extNameLower.includes("postnl") || extNameLower.includes("post-nl") || extNameLower.includes("post nl"))
            ? "PostNL"
            : shipment?.externalShipperName;
    } else if (type === 'sendcloud') {
        type = extNameLower.includes("ups") ? "ups" : "dhl";
        name = shipment?.externalShipperName;
    }

    return { name, type };
};

// Height of the visible sliver of every stacked card: tight like fanned playing cards,
// just enough to read the shipper logo and tracking number.
const PEEK_HEIGHT = 26;

// Shown in place of the warehouse "shipping_by" block. The primary (most recent) shipment
// is a full card in the warehouse "shipping_by" design; any other shipments fan out
// underneath it, each showing a sliver with its shipper logo and tracking number in its
// own shipper background color. Clicking opens the shipments view.
const ShipmentsStack = ({ shipments, onOpenShipments }) => {
    const [primary, ...others] = shipments;
    const primaryShipper = displayShipper(primary);
    const colorSettings = getShipperColorSettingsByType(primaryShipper.type);

    return (
        <ButtonBase
            onClick={onOpenShipments}
            sx={{
                mt:1,
                display:"block",
                textAlign:"left",
                borderRadius:4,
                position:"relative",
                mb: others.length > 0 ? `${others.length * PEEK_HEIGHT}px` : 0,
            }}
        >
            {others.map((shipment, idx) => {
                const peekShipper = displayShipper(shipment);
                const peekColors = getShipperColorSettingsByType(peekShipper.type);
                return (
                    <Box
                        key={`shipment-peek-${shipment.id}`}
                        className="shipment-peek"
                        sx={{
                            position:"absolute",
                            left: 0,
                            right: 0,
                            bottom: (idx + 1) * -PEEK_HEIGHT,
                            height: PEEK_HEIGHT + 20,
                            borderRadius:4,
                            background: peekColors.background,
                            border: `1px solid ${peekColors.streamrulehover}`,
                            boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.15)",
                            zIndex: others.length - idx,
                        }}
                    >
                        <Box
                            sx={{
                                position:"absolute",
                                left:0,
                                right:0,
                                bottom:0,
                                height: PEEK_HEIGHT,
                                px:1.5,
                                display:"flex",
                                alignItems:"center",
                                gap:1,
                                overflow:"hidden",
                                "& svg" : { width:16, height:16, flexShrink:0 },
                            }}
                        >
                            {shipment.shipper &&
                                getShipperLogo(peekShipper.type)
                            }
                            <Box
                                sx={{
                                    fontSize:11,
                                    fontFamily:"Roboto, sans-serif",
                                    color: tokens("light").txt["primary"],
                                    whiteSpace:"nowrap",
                                    overflow:"hidden",
                                    textOverflow:"ellipsis",
                                }}
                            >
                                {shipment.trackingNumber}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
            <Box
                sx={{
                    position:"relative",
                    zIndex: others.length + 1,
                    background: colorSettings.background,
                    borderRadius:4,
                    p:2,
                    display:'flex',
                    gap:1.5,
                    boxShadow: others.length > 0 ? "0px 2px 4px rgba(0, 0, 0, 0.12)" : "none",
                    "& .postnl, & .dhl, & .dpd, & .dhlgermany, & .cirro" : { width:24, height:24 },
                    "& .bol" : { width:32, height:16 },
                    "& .hst" : { width:72, height:24 },
                }}
            >
                {primary.shipper &&
                    getShipperLogo(primaryShipper.type)
                }
                <Box>
                    <Text bold color={tokens("light").txt["primary"]}>{primaryShipper.name}</Text><br/>
                    <Text color={tokens("light").txt["primary"]}>{primary.trackingNumber}</Text>
                </Box>
            </Box>
        </ButtonBase>
    );
};

export default ShipmentsStack;
